import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  MenuItem,
  Typography,
  Avatar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  Drawer,
} from '@mui/material';
import ErpDataGrid, { GeneralItemsGridFrame } from '../../components/common/ErpDataGrid';
import ListDetailOpenModeBar from '../../components/common/ListDetailOpenModeBar';
import ListRowActionsBar from '../../components/common/ListRowActionsBar';
import useListDetailOpenMode from '../../hooks/useListDetailOpenMode';
import ContactDetails from './ContactDetails';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as CollapseTypesIcon,
  PictureAsPdf as PdfIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { standardAuditColumns } from '../../utils/auditGridColumns';
import { copyAppPathToClipboard, openAppPathInNewTab } from '../../utils/listScreenHelpers';
import { effectiveContactRoles, primaryContactTypeFromRoles, TYPE_LABELS, CONTACT_TYPE_OPTIONS } from './contactRolesUtil';

const CONTACT_TYPE_LABELS = TYPE_LABELS;

const ContactsList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { mode, persistMode, popupId, setPopupId, sideId, setSideId, openRow } = useListDetailOpenMode('contacts');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ contact_type: [], status: '' });
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });
  const [selectedContact, setSelectedContact] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteMode, setDeleteMode] = useState('permanent');
  /** Contact rows where Type column shows all roles (vs primary-only one line). */
  const [expandedContactTypes, setExpandedContactTypes] = useState(() => new Set());

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        search,
        ...(filters.status ? { status: filters.status } : {}),
      };
      if (Array.isArray(filters.contact_type) && filters.contact_type.length > 0) {
        params.contact_type = filters.contact_type.join(',');
      }
      const response = await api.get('/contacts', { params });
      setContacts(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      console.error('Fetch contacts error:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, search, filters]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleDelete = async () => {
    if (!selectedContact) return;
    try {
      const res =
        deleteMode === 'deactivate'
          ? await api.put(`/contacts/${selectedContact.id}`, { status: 'inactive' }, { skipErrorToast: true })
          : await api.delete(`/contacts/${selectedContact.id}`, { skipErrorToast: true });
      toast.success(
        res.data?.message ||
          (deleteMode === 'deactivate' ? 'Contact set to inactive.' : 'Contact removed.')
      );
      fetchContacts();
    } catch (error) {
      const body = error.response?.data;
      const msg =
        body?.message ||
        body?.errors?.[0]?.msg ||
        (typeof error.message === 'string' && !error.message.startsWith('Request failed')
          ? error.message
          : null) ||
        'Failed to delete contact';
      toast.error(msg);
    }
    setDeleteDialog(false);
    setSelectedContact(null);
  };

  const contactTypeColors = {
    regular: '#6b7280',
    candidate: '#6366f1',
    employee: '#10b981',
    contractor: '#3b82f6',
    contractor_independent: '#3b82f6',
    contractor_c2c: '#0ea5e9',
    company: '#f59e0b',
    trainee: '#8b5cf6',
    guest_signedup: '#94a3b8',
    trainer: '#d946ef',
  };

  const statusColors = {
    active: '#10b981',
    inactive: '#6b7280',
    blacklisted: '#ef4444',
    deleted: '#b45309',
    archived: '#7c3aed'
  };

  const statusDisplay = (row) => {
    if (row.archived_at) return { label: 'Archived', key: 'archived' };
    if (row.status === 'deleted' || row.is_marked_for_deletion) return { label: 'Deleted', key: 'deleted' };
    const s = row.status || 'active';
    return { label: s.charAt(0).toUpperCase() + s.slice(1), key: s };
  };

  const columns = [
    {
      field: 'contact_code',
      headerName: 'Reference',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" sx={{ fontWeight: 500 }}>
          {params.value || '–'}
        </Typography>
      )
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.9rem' }}>
            {params.row.first_name?.[0]}{params.row.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params.row.first_name} {params.row.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 260,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const detailPath = `/contacts/${row.id}`;
        return (
          <ListRowActionsBar
            menuItems={[
              {
                label: 'Open in new tab',
                icon: <OpenInNewIcon fontSize="small" />,
                onClick: () => openAppPathInNewTab(detailPath),
              },
              {
                label: 'Copy link',
                icon: <CopyIcon fontSize="small" />,
                onClick: () => copyAppPathToClipboard(detailPath),
              },
            ]}
          >
            {hasPermission('contacts_modify') && (
              <Tooltip title="Edit">
                <IconButton size="small" color="primary" onClick={() => navigate(`${detailPath}/edit`)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View">
              <IconButton size="small" onClick={() => navigate(detailPath)}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in new tab for print or Save as PDF">
              <IconButton size="small" color="secondary" onClick={() => openAppPathInNewTab(detailPath)}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {hasPermission('contacts_delete') && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedContact(row);
                    setDeleteMode('permanent');
                    setDeleteDialog(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </ListRowActionsBar>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140
    },
    {
      field: 'contact_type',
      headerName: 'Type',
      width: 190,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const rowId = row?.id != null ? String(row.id) : '';
        const roles = effectiveContactRoles(row);
        const primary = primaryContactTypeFromRoles(roles);
        const expanded = rowId ? expandedContactTypes.has(rowId) : false;

        const labelFor = (role) =>
          CONTACT_TYPE_LABELS[role] || String(role || '').replace(/_/g, ' ');

        const chipSx = (role) => {
          const color = contactTypeColors[role] || '#64748b';
          return {
            bgcolor: `${color}20`,
            color,
            fontWeight: 500,
            fontSize: '0.7rem',
            height: 22,
          };
        };

        const typeChip = (role) => (
          <Chip key={role} label={labelFor(role)} size="small" sx={chipSx(role)} />
        );

        const toggleExpand = (e) => {
          e.stopPropagation();
          if (!rowId) return;
          setExpandedContactTypes((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
          });
        };

        if (roles.length <= 1) {
          return typeChip(roles[0] || 'regular');
        }

        if (!expanded) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
              {typeChip(primary)}
              <Tooltip title="Show all contact types">
                <IconButton size="small" onClick={toggleExpand} sx={{ p: 0.35 }} aria-label="Show all contact types">
                  <ViewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'wrap',
              py: 0.25,
              width: '100%',
            }}
          >
            {roles.map((r) => typeChip(r))}
            <Tooltip title="Show primary type only">
              <IconButton size="small" onClick={toggleExpand} sx={{ p: 0.35 }} aria-label="Collapse contact types">
                <CollapseTypesIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const { label, key } = statusDisplay(params.row);
        const color = statusColors[key] || statusColors.inactive;
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              bgcolor: `${color}20`,
              color,
              fontWeight: 500,
              fontSize: '0.7rem'
            }}
          />
        );
      }
    },
    {
      field: 'work_authorization',
      headerName: 'Work Auth',
      width: 120,
      renderCell: (params) => params.value?.toUpperCase().replace('_', ' ') || '-'
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 180,
      renderCell: (params) => {
        const accounts = params.row.Accounts || [];
        if (accounts.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {accounts[0].company_name}
            </Typography>
            {accounts.length > 1 && (
              <Typography variant="caption" color="text.secondary">
                +{accounts.length - 1} more
              </Typography>
            )}
          </Box>
        );
      }
    },
    ...standardAuditColumns(),
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }} data-testid="contacts-page-title">
            Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your contacts, candidates, and client contacts
          </Typography>
        </Box>
        {hasPermission('contacts_add') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/contacts/new')}
            data-testid="contacts-add-button"
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            }}
          >
            Add Contact
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }} data-testid="contacts-filters">
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            inputProps={{ 'data-testid': 'contacts-search' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Type</InputLabel>
            <Select
              multiple
              displayEmpty
              data-testid="contacts-type-filter"              value={filters.contact_type}
              label="Type"
              onChange={(e) => {
                const v = e.target.value;
                setFilters({
                  ...filters,
                  contact_type: typeof v === 'string' ? [] : [...v],
                });
              }}
              renderValue={(selected) =>
                !selected || selected.length === 0
                  ? 'All types'
                  : selected.map((id) => CONTACT_TYPE_LABELS[id] || id).join(', ')
              }
            >
              {CONTACT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.code} value={opt.code}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="blacklisted">Blacklisted</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <ListDetailOpenModeBar value={mode} onChange={persistMode} sx={{ flex: '1 1 auto' }} />
        </Box>
      </Card>

      {/* Data Grid */}
      <Card sx={{ overflow: 'hidden' }}>
        <GeneralItemsGridFrame>
          <ErpDataGrid
            listApplet
            columnOrderStorageKey="general-items-contacts"
            rows={contacts}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={pagination.total}
            paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
            onPaginationModelChange={(model) => setPagination({ ...pagination, ...model })}
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            autoHeight={false}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
            onRowClick={(params) => openRow(params.row, (r) => `/contacts/${r.id}`)}
          />
        </GeneralItemsGridFrame>
      </Card>

      <Drawer
        anchor="right"
        open={mode === 'side' && Boolean(sideId)}
        onClose={() => setSideId(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 720 }, maxWidth: '100vw' } }}
      >
        <Box sx={{ height: '100%', overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>
          {sideId ? (
            <ContactDetails detailId={sideId} embedded onCloseEmbedded={() => setSideId(null)} />
          ) : null}
        </Box>
      </Drawer>

      <Dialog fullWidth maxWidth="lg" open={Boolean(popupId)} onClose={() => setPopupId(null)} scroll="paper">
        <DialogContent sx={{ p: 0 }}>
          {popupId ? (
            <ContactDetails detailId={popupId} embedded onCloseEmbedded={() => setPopupId(null)} />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => { setDeleteDialog(false); setSelectedContact(null); }}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Choose how you want to remove "{selectedContact?.first_name} {selectedContact?.last_name}".
          </Typography>
          <RadioGroup value={deleteMode} onChange={(e) => setDeleteMode(e.target.value)}>
            <FormControlLabel
              value="deactivate"
              control={<Radio />}
              label="Only deactivate contact (keep record)."
              disabled={!hasPermission('contacts_modify')}
            />
            <FormControlLabel
              value="permanent"
              control={<Radio />}
              label="Delete permanently (respects Record Deletion Retaining Days setting)."
            />
          </RadioGroup>
          {!hasPermission('contacts_modify') && (
            <Typography variant="caption" color="text.secondary">
              Deactivate requires contact modify permission; use permanent delete if your role allows only delete.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialog(false); setSelectedContact(null); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactsList;
